export type NavView = 'deck' | 'perspectives' | 'sessions' | 'patterns' | 'lab' | 'policy';
export type EscalationState = 'idle' | 'active' | 'resolved';
export type InterventionResponse = 'accept' | 'snooze' | 'dismiss';

export interface DemoState {
  view: NavView;
  hour: number;
  playing: boolean;
  escalation: EscalationState;
  selectedPatternId: string | null;
  selectedSessionId: string | null;
  lastResponse: InterventionResponse | null;
  recoveryVisible: boolean;
  policyConfidence: number;
  suppressedInterventions: string[];
  presentationStep: number;
  evidenceOpen: boolean;
  policyApplied: boolean;
}

export type DemoAction =
  | { type: 'navigate'; view: NavView }
  | { type: 'seek'; hour: number }
  | { type: 'toggle-play' }
  | { type: 'tick'; amount?: number }
  | { type: 'respond'; response: InterventionResponse }
  | { type: 'select-pattern'; id: string }
  | { type: 'select-session'; id: string }
  | { type: 'set-evidence'; open: boolean }
  | { type: 'presentation-next' }
  | { type: 'presentation-start' }
  | { type: 'toggle-policy' }
  | { type: 'reset' };

const START_HOUR = 8.4;
const INTERVENTION_THRESHOLD = 12.75;

export function createInitialState(): DemoState {
  return {
    view: 'deck',
    hour: START_HOUR,
    playing: false,
    escalation: 'idle',
    selectedPatternId: null,
    selectedSessionId: null,
    lastResponse: null,
    recoveryVisible: false,
    policyConfidence: 62,
    suppressedInterventions: [],
    presentationStep: 0,
    evidenceOpen: false,
    policyApplied: false,
  };
}

function seek(state: DemoState, hour: number): DemoState {
  const boundedHour = Math.min(14, Math.max(0, hour));
  const shouldEscalate =
    boundedHour >= INTERVENTION_THRESHOLD &&
    state.escalation === 'idle' &&
    !state.suppressedInterventions.includes('boundary-reset');

  return {
    ...state,
    hour: boundedHour,
    escalation: shouldEscalate ? 'active' : state.escalation,
    selectedPatternId: shouldEscalate ? 'recovery-deferral' : state.selectedPatternId,
    evidenceOpen: shouldEscalate ? false : state.evidenceOpen,
    playing: boundedHour >= 14 ? false : state.playing,
  };
}

export function demoReducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    case 'navigate':
      return { ...state, view: action.view, evidenceOpen: false };
    case 'seek':
      return seek(state, action.hour);
    case 'toggle-play':
      return { ...state, playing: !state.playing };
    case 'tick':
      return seek(state, state.hour + (action.amount ?? 0.15));
    case 'respond': {
      if (action.response === 'accept') {
        return {
          ...state,
          escalation: 'resolved',
          lastResponse: action.response,
          recoveryVisible: true,
          policyConfidence: Math.min(99, state.policyConfidence + 14),
          evidenceOpen: false,
          view: 'policy',
        };
      }
      if (action.response === 'dismiss') {
        return {
          ...state,
          escalation: 'resolved',
          lastResponse: action.response,
          recoveryVisible: false,
          policyConfidence: Math.max(0, state.policyConfidence - 9),
          suppressedInterventions: Array.from(
            new Set([...state.suppressedInterventions, 'boundary-reset']),
          ),
          evidenceOpen: false,
        };
      }
      return {
        ...state,
        escalation: 'resolved',
        lastResponse: action.response,
        recoveryVisible: false,
        evidenceOpen: false,
      };
    }
    case 'select-pattern':
      return { ...state, selectedPatternId: action.id, evidenceOpen: true };
    case 'select-session':
      return { ...state, selectedSessionId: action.id, evidenceOpen: true };
    case 'set-evidence':
      return { ...state, evidenceOpen: action.open };
    case 'presentation-start':
      return { ...createInitialState(), playing: true, presentationStep: 1 };
    case 'presentation-next': {
      const step = Math.min(5, state.presentationStep + 1);
      const viewByStep: NavView[] = ['deck', 'deck', 'patterns', 'deck', 'policy', 'lab'];
      const next = { ...state, presentationStep: step, view: viewByStep[step] };
      return step === 3 ? seek(next, 13.2) : next;
    }
    case 'toggle-policy':
      return { ...state, policyApplied: !state.policyApplied };
    case 'reset':
      return createInitialState();
  }
}
